import { useEffect } from "react";
import { toConfiguredAbsoluteUrl } from "../config/api";
import categoryShareImage from "../assets/category-bg.jpg";

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.categorySeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.categorySeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.categorySeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.categorySeo = "true";
}

function upsertJsonLd(data) {
  const id = "category-jsonld";
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function CategorySeo({ contact, categories }) {
  useEffect(() => {
    const siteName = String(contact?.siteName || "").trim();
    const pageUrl = toConfiguredAbsoluteUrl("/category");
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const imageUrl = toConfiguredAbsoluteUrl(categoryShareImage);

    const title = siteName
      ? `Product Categories | Steel & Industrial Supplies Malaysia | ${siteName}`
      : "Product Categories | Steel & Industrial Supplies Malaysia";
    const description = siteName
      ? `Browse steel, fasteners, nuts and bolts, roofing, paint and industrial supply categories from ${siteName}. Supplying contractors across Malaysia.`
      : "Browse steel, fasteners, nuts and bolts, roofing, paint and industrial supply categories. Supplying contractors across Malaysia.";
    const ogTitle = siteName ? `Product Categories | ${siteName}` : "Product Categories";
    const ogDescription =
      "Browse steel, hardware and industrial supply categories for contractors and businesses across Malaysia.";

    document.title = title;
    upsertMeta("name", "description", description);
    upsertLink("canonical", pageUrl);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:url", pageUrl);
    upsertMeta("property", "og:image", imageUrl);
    if (siteName) upsertMeta("property", "og:site_name", siteName);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", ogDescription);
    upsertMeta("name", "twitter:image", imageUrl);

    const collectionPage = {
      "@type": "CollectionPage",
      "@id": pageUrl ? `${pageUrl}#webpage` : undefined,
      name: title,
      description,
      url: pageUrl || undefined,
    };
    Object.keys(collectionPage).forEach((key) => {
      if (collectionPage[key] === undefined) delete collectionPage[key];
    });

    const breadcrumbList = {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: homeUrl || undefined,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Product Categories",
          item: pageUrl || undefined,
        },
      ],
    };

    const published = Array.isArray(categories) ? categories.filter((item) => item?.slug && item?.categoryName) : [];
    const graph = [collectionPage, breadcrumbList];
    if (published.length) {
      graph.push({
        "@type": "ItemList",
        itemListElement: published.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.categoryName,
          url: toConfiguredAbsoluteUrl(`/category/${item.slug}`),
        })),
      });
    }

    upsertJsonLd({
      "@context": "https://schema.org",
      "@graph": graph,
    });

    return () => {
      document.head.querySelectorAll("[data-category-seo='true']").forEach((el) => el.remove());
      const script = document.getElementById("category-jsonld");
      if (script) script.remove();
    };
  }, [contact, categories]);

  return null;
}

export default CategorySeo;
